import { BrandingColors, CustomerProfiles } from '../../tenants/types';
import { customerProfile } from '../customer/customer-profile';

/** Variables CSS de la marca (las leen `styles/_colors.scss`, `styles.scss` y algunos componentes). */
export enum BrandingCssVar {
    Primary = '--tenant-primary',
    Info = '--tenant-info',
    BrandBackground = '--tenant-brand-background',
    LogoBackground = '--tenant-logo-background',
    LogoPadding = '--tenant-logo-padding'
}

/** Relleno del recuadro del logo cuando hay `logoBackground`. */
const LOGO_BOX_PADDING = '4px 10px';

/**
 * Colores del header y del tema para una aseguradora (`customer` de `/me/`): los `colors` del tenant con lo que
 * defina `customers[customerId].colors` encima (solo las claves con valor). Sin customer, sin mapa, con un customer
 * desconocido o sin `colors` en su perfil → `colors` del tenant. Función pura: no toca el DOM.
 */
export const resolveCustomerColors = (
    colors: BrandingColors,
    customers: CustomerProfiles | undefined,
    customerId: number | null | undefined
): BrandingColors => {
    const base: BrandingColors = { ...colors };
    const profile = customerProfile(customers, customerId);
    const override = profile && profile.colors;
    if (!override) {
        return base;
    }
    (Object.keys(override) as Array<keyof BrandingColors>).forEach(key => {
        const value = override[key];
        if (value) {
            base[key] = value;
        }
    });
    return base;
};

/** Lo mínimo del servicio de tema de Vuetify 2 (`vuetify.framework.theme`, igual a `this.$vuetify.theme`). */
export interface ThemeTarget {
    themes: { light: Record<string, any> };
}

/**
 * Aplica los colores en tiempo de ejecución:
 * - variables CSS en `root` (`--tenant-primary`, `--tenant-info`, `--tenant-brand-background` y, si hay
 *   `logoBackground`, `--tenant-logo-background` / `--tenant-logo-padding`; si no, se quitan);
 * - `primary` / `info` del tema claro de Vuetify (reactivo en Vuetify 2: regenera los estilos del tema).
 */
export const applyBrandingColors = (
    colors: BrandingColors,
    target: { root?: HTMLElement; theme?: ThemeTarget | null } = {}
): void => {
    const root = target.root || document.documentElement;
    root.style.setProperty(BrandingCssVar.Primary, colors.primary);
    root.style.setProperty(BrandingCssVar.Info, colors.info);
    root.style.setProperty(BrandingCssVar.BrandBackground, colors.brandBackground);
    if (colors.logoBackground) {
        root.style.setProperty(BrandingCssVar.LogoBackground, colors.logoBackground);
        root.style.setProperty(BrandingCssVar.LogoPadding, LOGO_BOX_PADDING);
    } else {
        // Al volver a una aseguradora sin recuadro (o a los colores del tenant), el logo queda como siempre.
        root.style.removeProperty(BrandingCssVar.LogoBackground);
        root.style.removeProperty(BrandingCssVar.LogoPadding);
    }
    if (target.theme) {
        const light = target.theme.themes.light;
        if (light.primary !== colors.primary) {
            light.primary = colors.primary;
        }
        if (light.info !== colors.info) {
            light.info = colors.info;
        }
    }
};
